// apps/admin/src/stores/usePreferencesStore.ts
//
// Phase 8.1 Wave 2 Task 08 — admin user UI preferences (theme,
// language, timezone) with localStorage persistence + server-side
// hydrate hook for cross-device parity.
//
// Storage rules:
//   • Local-first — UI never blocks on the server. Reads come from
//     localStorage on boot, falling back to safe defaults (light / vi /
//     UTC). Writes persist locally synchronously + optionally fire a
//     PATCH `/me/preferences` so the BE remembers across devices.
//   • A simple `hydrateFromServer(payload)` helper lets the auth
//     bootstrap call into this store after `/me` returns the user's
//     server-side preferences. Server values win on a fresh login;
//     local values win during the session (the user just changed it).
//
// vg-binding: PLAN:task-08
// vg-binding: P8.D-026
// vg-binding: P8.D-165
// vg-binding: P8.D-174

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { apiClient } from '../lib/apiClient.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type Theme = 'light' | 'dark' | 'system';

export interface PreferencesState {
  theme: Theme;
  language: string;
  timezone: string;
  /** Local-only flag — true once we've successfully overwritten from `/me`. */
  hydrated: boolean;

  setTheme: (theme: Theme) => void;
  setLanguage: (lang: string) => void;
  setTimezone: (tz: string) => void;
  /**
   * Apply server-side preferences (typically called by `useAuthStore`
   * after `/me` lands). No-op when input fields are nullish so partial
   * server payloads don't overwrite local-only changes.
   */
  hydrateFromServer: (
    server: Partial<Pick<PreferencesState, 'theme' | 'language' | 'timezone'>>,
  ) => void;
  /** Best-effort PATCH `/me/preferences`. Errors are swallowed. */
  persistToServer: () => Promise<void>;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Vietnamese-first per project language policy. The shared i18n
 * package falls back to `en` when a key is missing.
 */
const DEFAULT_LANGUAGE = 'vi';

/**
 * UTC as the canonical neutral default. Admins reset this to their
 * local IANA zone (e.g. `Asia/Ho_Chi_Minh`) via Settings → Preferences.
 */
const DEFAULT_TIMEZONE = 'UTC';

const DEFAULT_THEME: Theme = 'light';

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      theme: DEFAULT_THEME,
      language: DEFAULT_LANGUAGE,
      timezone: DEFAULT_TIMEZONE,
      hydrated: false,

      setTheme(theme: Theme) {
        set({ theme });
        void get().persistToServer();
      },

      setLanguage(language: string) {
        set({ language });
        void get().persistToServer();
      },

      setTimezone(timezone: string) {
        set({ timezone });
        void get().persistToServer();
      },

      hydrateFromServer(server) {
        const next: Partial<PreferencesState> = {};
        if (
          server.theme &&
          (server.theme === 'light' ||
            server.theme === 'dark' ||
            server.theme === 'system')
        ) {
          next.theme = server.theme;
        }
        if (typeof server.language === 'string' && server.language.length > 0) {
          next.language = server.language;
        }
        if (typeof server.timezone === 'string' && server.timezone.length > 0) {
          next.timezone = server.timezone;
        }
        set({ ...next, hydrated: true });
      },

      async persistToServer() {
        const { theme, language, timezone } = get();
        try {
          await apiClient.patch('/me/preferences', {
            themePreference: theme,
            language,
            timezone,
          });
        } catch {
          // Best-effort — local state is authoritative for the session.
        }
      },

      reset() {
        set({
          theme: DEFAULT_THEME,
          language: DEFAULT_LANGUAGE,
          timezone: DEFAULT_TIMEZONE,
          hydrated: false,
        });
      },
    }),
    {
      name: 'pw_admin_prefs_v1',
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? window.localStorage : nullStorage,
      ),
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
        timezone: state.timezone,
      }),
    },
  ),
);

/**
 * SSR / non-browser fallback so `persist` doesn't blow up when
 * `localStorage` is missing (Node, vitest jsdom). The shape mirrors
 * the Web Storage API but every operation is a no-op.
 */
const nullStorage: Storage = {
  getItem(_key: string): string | null {
    return null;
  },
  setItem(_key: string, _value: string): void {
    /* no-op */
  },
  removeItem(_key: string): void {
    /* no-op */
  },
  clear(): void {
    /* no-op */
  },
  key(_index: number): string | null {
    return null;
  },
  length: 0,
};

export default usePreferencesStore;
